'use client';

import { ButtonProps } from '@tuic/web-compositions/core/Button';
import { FC, useState } from 'react';
import { GetSlashCredentials } from '@lib/third-parties/get-slash.types';
import { GetSlashInternetState, StartSessionResult } from '../lib/use-get-slash-internet';
import { Locale } from '@tuic/i18n';
import { NetworkAccessType } from '@modules/get-slash-internet-card/lib/use-network-access-type';
import type { InternetCardViewData, GetInternetDataCta } from '@modules/get-slash-internet-card/lib/internet-card-view-data';
import { GeneralView } from '@modules/get-slash-internet-card/components/dialog-views/general-view';
import { SmartLink } from '../../../app/[locale]/[bookingCode]/_components/smart-link';
import { BookingView } from '@modules/get-slash-internet-card/components/dialog-views/booking-view';
import { createDocumentRenderer, createTextRenderer, renderTermsOfUseCheckboxText } from '@modules/get-slash-internet-card/lib/utils';
import { ConsumptionView } from '@modules/get-slash-internet-card/components/dialog-views/consumption-view';
import { ExhaustedView } from '@modules/get-slash-internet-card/components/dialog-views/exhausted-view';
import { useIsMobile } from '../../../lib/hooks/use-is-mobile';
export type RefreshDialogHandler = (opts?: {
  indicator?: boolean;
}) => Promise<void>;
type Props = {
  locale: Locale;
  viewData: InternetCardViewData;
  getSlashCredentials: GetSlashCredentials;
  useDebugEndpoints: boolean;
  getSlashInternet: GetSlashInternetState;
  networkAccessType: NetworkAccessType | undefined;
};
export const DialogViewRenderer: FC<Props> = props => {
  const {
    locale,
    getSlashCredentials,
    useDebugEndpoints,
    viewData,
    getSlashInternet,
    networkAccessType
  } = props;
  const [pendingDialog, setPendingDialog] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(false);
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const isMobile = useIsMobile();
  const {
    session,
    startSession,
    endSession,
    refreshSession
  } = getSlashInternet;
  const refreshDialog: RefreshDialogHandler = async opts => {
    if (opts?.indicator) {
      setPendingDialog(true);
    }
    await refreshSession();
    setPendingDialog(false);
  };
  if (pendingDialog) {
    return <GeneralView indicating />;
  }
  const handleTariffToggle = async (): Promise<StartSessionResult | undefined> => {
    const {
      active,
      tariffName
    } = session ?? {};
    if (typeof active === 'undefined' || typeof tariffName === 'undefined') {
      return undefined;
    }
    setPendingToggle(true);
    if (active) {
      await endSession();
      setPendingToggle(false);
      return undefined;
    }
    const result = await startSession();
    setPendingToggle(false);
    return result;
  };
  const handleRefresh = async () => {
    if (typeof refreshSession === 'undefined') {
      return;
    }
    setPendingRefresh(true);
    await refreshSession();
    setPendingRefresh(false);
  };
  const placeholders = {
    tariff_name: session?.tariffName,
    wifi_name: viewData.shipConfiguration?.wifiName
  };
  const renderText = createTextRenderer(placeholders);
  const renderDocument = createDocumentRenderer(placeholders);
  if (networkAccessType !== NetworkAccessType.Ship) {
    return <GeneralView title={renderText(viewData.connectView?.headline)} text={renderDocument(viewData.connectView?.text)} buttons={transformCtasToButtonProps(viewData.connectView?.ctas)}
    // Set custom variant for Look25 button since the props are from Contentful
    customVariant="primary" />;
  }

  // show booking view if no tariff is booked or remaining time is 0 (for time-based tariffs; -1 on non-time-based tariffs)
  if (typeof session === 'undefined' || !session.hasBookedTariff || session.remainingTime === 0) {
    return <BookingView locale={locale} getSlashCredentials={getSlashCredentials} useDebugEndpoints={useDebugEndpoints} data={viewData?.bookingView} textRenderer={renderText} documentRenderer={renderDocument} refreshDialog={refreshDialog} termsOfUseCheckbox={viewData.termsOfUseCheckbox ? {
      label: renderTermsOfUseCheckboxText(viewData.termsOfUseCheckbox.text),
      errorMessage: viewData.termsOfUseCheckbox.error
    } : undefined} />;
  }

  // show custom view for tariffs with exhausted data volume
  if (session.remainingVolume === 0) {
    return <ExhaustedView data={viewData.exhaustedView} session={session} locale={locale} getSlashCredentials={getSlashCredentials} useDebugEndpoints={useDebugEndpoints} documentRenderer={renderDocument} textRenderer={renderText} refreshDialog={refreshDialog} termsOfUseCheckbox={viewData.termsOfUseCheckbox ? {
      label: renderTermsOfUseCheckboxText(viewData.termsOfUseCheckbox.text),
      errorMessage: viewData.termsOfUseCheckbox.error
    } : undefined} />;
  }
  return <ConsumptionView data={viewData.consumptionView} session={session} networkAccessType={networkAccessType} handleRefresh={handleRefresh} handleTariffToggle={handleTariffToggle} pendingRefresh={pendingRefresh} pendingToggle={pendingToggle} textRenderer={renderText} documentRenderer={renderDocument} isMobile={isMobile} // Pass the boolean to the sub-component
  data-sentry-element="ConsumptionView" data-sentry-component="DialogViewRenderer" data-sentry-source-file="dialog-view-renderer.tsx" />;
};
const transformCtasToButtonProps = (ctas: GetInternetDataCta[] | undefined): ButtonProps[] => {
  return (ctas ?? []).map(cta => ({
    variant: cta.variant,
    asChild: true,
    children: <SmartLink href={cta.link}>{cta.label}</SmartLink>
  }));
};